import { z } from "@hono/zod-openapi";

import { uuidV7Schema } from "./identifiers";
import { invitationIssueSchema } from "./management";

export const billingPlanSchema = z.enum([
  "solo",
  "teams",
  "partner",
  "platform",
]);
export const billingStatusSchema = z.enum([
  "incomplete",
  "active",
  "past_due",
  "canceled",
]);
export const billingModeSchema = z.enum(["stripe", "local"]);

/** Unpaid days after Solo/Teams Checkout collects a payment method. */
export const selfServeTrialPeriodDays = 7;

export const partnerBillingRoleSchema = z.enum([
  "partner_owner",
  "partner_admin",
]);

const organizationNameSchema = z.string().trim().min(1).max(100);
const organizationSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;
const organizationSlugMaxLength = 63;
const organizationSlugFallback = "workspace";
const organizationSlugSchema = z
  .string()
  .trim()
  .min(3)
  .max(organizationSlugMaxLength)
  .regex(organizationSlugPattern);

export function organizationSlugFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, organizationSlugMaxLength)
    .replace(/-+$/gu, "");
  if (slug.length >= 3 && organizationSlugPattern.test(slug)) {
    return slug;
  }
  return organizationSlugFallback;
}

export function nextAvailableOrganizationSlug(
  preferred: string,
  taken: ReadonlySet<string>,
): string {
  const base = organizationSlugFromName(preferred);
  if (!taken.has(base)) return base;
  for (let n = 2; n < 10_000; n += 1) {
    const suffix = `-${String(n)}`;
    const candidate = `${base.slice(0, organizationSlugMaxLength - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base.slice(0, organizationSlugMaxLength - 5)}-${String(Date.now()).slice(-4)}`;
}

export const billingCheckoutRequestSchema = z
  .union([
    z
      .object({
        plan: z.enum(["solo", "teams"]),
        seats: z.number().int().min(2).max(500).optional(),
        organization_name: organizationNameSchema,
        organization_slug: organizationSlugSchema.optional(),
      })
      .refine(
        (value) => value.plan === "solo" || value.seats !== undefined,
        "teams requires seats",
      ),
    z
      .object({
        plan: z.literal("partner"),
        invite_code: z
          .string()
          .trim()
          .min(1)
          .max(100)
          .regex(/^[a-zA-Z0-9]+$/u),
        organization_name: organizationNameSchema,
        organization_slug: organizationSlugSchema.optional(),
      })
      .strict(),
  ])
  .openapi("BillingCheckoutRequest");

export const billingCheckoutResponseSchema = z
  .object({
    session_id: uuidV7Schema,
    checkout_url: z.string().nullable(),
    billing_mode: billingModeSchema,
  })
  .openapi("BillingCheckoutResponse");

export const billingCompleteRequestSchema = z
  .object({ session_id: uuidV7Schema })
  .openapi("BillingCompleteRequest");

export const billingCheckoutKindSchema = z.enum([
  "subscription",
  "credits",
  "resubscribe",
]);

export const billingCompleteResponseSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    plan: billingPlanSchema,
    status: billingStatusSchema,
    kind: billingCheckoutKindSchema,
  })
  .openapi("BillingCompleteResponse");

export const billingCreditsCheckoutRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    pack: z.literal(100),
    organization_id: uuidV7Schema.optional(),
  })
  .openapi("BillingCreditsCheckoutRequest");

export const billingPortalRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    organization_id: uuidV7Schema.optional(),
  })
  .openapi("BillingPortalRequest");

export const billingResubscribeRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    organization_id: uuidV7Schema.optional(),
  })
  .openapi("BillingResubscribeRequest");

export const billingPlanChangeRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    plan: z.enum(["solo", "teams"]),
    seats: z.number().int().min(2).max(500).optional(),
    organization_id: uuidV7Schema.optional(),
  })
  .refine(
    (value) => value.plan === "solo" || value.seats !== undefined,
    "teams requires seats",
  )
  .openapi("BillingPlanChangeRequest");

export const billingSeatsChangeRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    seats: z.number().int().min(2).max(500),
    organization_id: uuidV7Schema.optional(),
  })
  .openapi("BillingSeatsChangeRequest");

export const billingPortalResponseSchema = z
  .object({ portal_url: z.string() })
  .openapi("BillingPortalResponse");

export const billingUsageOrganizationSchema = z
  .object({
    organization_id: uuidV7Schema,
    name: z.string(),
    slug: z.string(),
    lifecycle_state: z.enum(["active", "suspended", "archived"]),
    seat_used: z.number().int().nonnegative(),
    connection_count: z.number().int().nonnegative(),
    credits_used: z.number().int().nonnegative(),
  })
  .openapi("BillingUsageOrganization");

export const billingUsageSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    plan: billingPlanSchema,
    status: billingStatusSchema,
    role: z.enum([
      "org_owner",
      "org_admin",
      "partner_owner",
      "partner_admin",
      "platform",
    ]),
    max_seats: z.number().int().nullable(),
    seat_used: z.number().int().nonnegative(),
    max_organizations: z.number().int().nullable(),
    organization_used: z.number().int().nonnegative(),
    max_vaults: z.number().int().nullable(),
    max_connections_per_principal: z.number().int().nullable(),
    allow_groups: z.boolean(),
    allow_team_vaults: z.boolean(),
    included_credits_per_period: z.number().int().nonnegative(),
    included_credits_used: z.number().int().nonnegative(),
    purchased_credits: z.number().int().nonnegative(),
    reserved_credits: z.number().int().nonnegative(),
    credits_remaining: z.number().int().nonnegative(),
    period_start: z.iso.datetime(),
    period_end: z.iso.datetime(),
    billing_mode: billingModeSchema,
    has_stripe_customer: z.boolean(),
    has_lifetime_access: z.boolean().optional(),
    organizations: z.array(billingUsageOrganizationSchema),
  })
  .openapi("BillingUsage");

export const billingAccountSummarySchema = z
  .object({
    billing_account_id: uuidV7Schema,
    plan: billingPlanSchema,
    status: billingStatusSchema,
    role: z.enum(["partner_owner", "partner_admin", "platform"]),
    home_organization_id: uuidV7Schema.nullable(),
    max_seats: z.number().int().nullable(),
    max_organizations: z.number().int().nullable(),
    included_credits_per_period: z.number().int().nonnegative(),
    included_credits_used: z.number().int().nonnegative(),
    purchased_credits: z.number().int().nonnegative(),
    reserved_credits: z.number().int().nonnegative(),
    credits_remaining: z.number().int().nonnegative(),
    period_start: z.iso.datetime(),
    period_end: z.iso.datetime(),
  })
  .openapi("BillingAccountSummary");

export const billingAccountListResponseSchema = z
  .object({ accounts: z.array(billingAccountSummarySchema) })
  .openapi("BillingAccountListResponse");

export const billingOrganizationCreateRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    name: organizationNameSchema,
    slug: organizationSlugSchema,
    first_owner_email: z
      .string()
      .trim()
      .max(254)
      .pipe(z.email())
      .transform((value) => value.toLowerCase())
      .optional(),
    invitation_expires_in_hours: z.number().int().min(1).max(168).default(72),
  })
  .openapi("BillingOrganizationCreateRequest");

export const billingOrganizationCreateResponseSchema = z
  .object({
    organization_id: uuidV7Schema,
    name: z.string(),
    slug: z.string(),
    first_owner_invitation: invitationIssueSchema.nullable(),
  })
  .openapi("BillingOrganizationCreateResponse");

export const operatorPartnerProvisionRequestSchema = z
  .object({
    name: organizationNameSchema,
    slug: organizationSlugSchema,
    first_owner_email: z
      .string()
      .trim()
      .max(254)
      .pipe(z.email())
      .transform((value) => value.toLowerCase()),
    invitation_expires_in_hours: z.number().int().min(1).max(168).default(72),
    max_seats: z.number().int().min(1).max(10_000).default(50),
    max_organizations: z.number().int().min(1).max(1_000).default(25),
    included_credits_per_period: z
      .number()
      .int()
      .min(0)
      .max(10_000_000)
      .optional(),
  })
  .openapi("OperatorPartnerProvisionRequest");

export const operatorPartnerProvisionResponseSchema = z
  .object({
    billing_account_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    first_owner_invitation: invitationIssueSchema,
  })
  .openapi("OperatorPartnerProvisionResponse");

export const operatorAttachBillingRequestSchema = z
  .object({
    billing_account_id: uuidV7Schema,
  })
  .openapi("OperatorAttachBillingRequest");

export type BillingCheckoutRequest = z.infer<
  typeof billingCheckoutRequestSchema
>;
export type BillingCheckoutResponse = z.infer<
  typeof billingCheckoutResponseSchema
>;
export type BillingCompleteRequest = z.infer<
  typeof billingCompleteRequestSchema
>;
export type BillingCompleteResponse = z.infer<
  typeof billingCompleteResponseSchema
>;
export type BillingCreditsCheckoutRequest = z.infer<
  typeof billingCreditsCheckoutRequestSchema
>;
export type BillingResubscribeRequest = z.infer<
  typeof billingResubscribeRequestSchema
>;
export type BillingPlanChangeRequest = z.infer<
  typeof billingPlanChangeRequestSchema
>;
export type BillingSeatsChangeRequest = z.infer<
  typeof billingSeatsChangeRequestSchema
>;
export type BillingUsage = z.infer<typeof billingUsageSchema>;
export type BillingAccountSummary = z.infer<typeof billingAccountSummarySchema>;
export type BillingOrganizationCreateRequest = z.infer<
  typeof billingOrganizationCreateRequestSchema
>;
export type OperatorPartnerProvisionRequest = z.infer<
  typeof operatorPartnerProvisionRequestSchema
>;
export type OperatorPartnerProvisionResponse = z.infer<
  typeof operatorPartnerProvisionResponseSchema
>;
